import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ipc/ipc-components';
import { Link, useLanguage } from '@ipc/ipc-core';
import { useFormOptions } from '../../hooks';
import { AppContentAdaptive, AppContentTitle } from '../../styles';
import { useValidations } from './hooks';
import { Loading } from '../../elements';
import ReportingDateRangeSection from './sections/ReportingDateRange';
import CarrierOperator from '../CreatePreSettlement/sections/CarrierOperator';
import {
  addMonthsToDate,
  dateToString, firstDayOfPreviousMonth, formatDate, isDateAfter, lastDayOfPreviousMonth,
} from '../../utils/date';
import { getReceptacleListTitle } from '../ReceptaclesList/constants';
import { RECEPTACLE_SEARCH_FORM_DATA } from './constants';
import { EMPTY_BAG_VALUE } from '../CreatePreSettlement/constants';
import { loadImpc } from '../../utils/impcUtil';

const ReceptacleSearchCriteria = ({ history: { push }, match: { params: { filter = '' } }, newWarning }) => {
  const [, language] = useLanguage();
  const fields = ['mail_class', 'dropdown_carrier', 'postal_operator', 'impc'];
  const [options, loaded, updateOptions] = useFormOptions(fields, true);
  const [isImpcSelected, setImpcSelected] = useState(false);
  const [submitted, updateSubmitted] = useState(false);
  const [values, updateValues] = useState({
    reportingStartDate: formatDate(firstDayOfPreviousMonth()),
    reportingStopDate: formatDate(lastDayOfPreviousMonth()),
    mailClass: [],
    impc: [],
  });
  const [isAllMailClassSelected, setAllMailClassSelected] = useState(false);
  const [operator, updateOperator] = useState({});
  const [validations] = useValidations(values, submitted);
  const [operatorCopy, updateOperatorCopy] = useState(operator);

  const changeImpcSelected = () => {
    if (options.impc && options.impc.length === 0) {
      setImpcSelected(false);
    } else if ((values.impc && values.impc.length) === (options.impc && options.impc.length)) {
      setImpcSelected(true);
    } else {
      setImpcSelected(false);
    }
  };

  useEffect(() => {
    const storageData = JSON.parse(localStorage.getItem(RECEPTACLE_SEARCH_FORM_DATA));
    if (storageData) {
      updateValues(storageData);
      if (loaded && options.mailClass.filter(item => item.value !== EMPTY_BAG_VALUE).length === storageData.mailClass.length) {
        setAllMailClassSelected(true);
      }
      if (storageData.operator) {
        updateOperatorCopy(storageData.operator);
      }
    }
  }, [loaded]);

  useEffect(() => {
    updateValues({ ...values, impc: [] });
    loadImpc(operator, options, updateOptions);
  }, [operator]);

  useEffect(() => {
    loadImpc(operatorCopy, options, updateOptions);
  }, [operatorCopy]);

  useEffect(() => {
    changeImpcSelected();
  }, [options.impc && options.impc.length]);

  useEffect(() => {
    changeImpcSelected();
  }, [values.impc && values.impc.length]);

  const changeDateValue = name => (selectedDay, modifiers, dayPickerInput) => {
    const input = dayPickerInput.getInput().value.trim();
    if (selectedDay && (selectedDay.getFullYear() <= 9999) && input && input !== '') {
      updateValues({ ...values, [name]: dateToString(selectedDay) });
    } else {
      updateValues({ ...values, [name]: null });
    }
  };

  const selectChange = (value, { name }) => {
    if (name === 'operator') {
      updateOperator(value);
    }
    updateValues({ ...values, [name]: value });
  };

  const allMailClassCheckboxHandler = (event, value) => {
    setAllMailClassSelected(value);
    updateValues({
      ...values,
      mailClass: value ? options.mailClass.filter(item => item.value !== EMPTY_BAG_VALUE) : [],
    });
  };
  const allImpcCheckBoxHandler = (event, value) => {
    setImpcSelected(value);
    updateValues({
      ...values,
      impc: value ? options.impc : [],
    });
  };
  const hasImpcOptions = () => Array.isArray(options.impc) && options.impc.length > 0;

  const constructUrlParams = () => {
    const urlParams = new URLSearchParams();
    if (values.operator) {
      urlParams.append('operator', values.operator.value);
    }
    if (values.carrier) {
      urlParams.append('carrier', values.carrier.value);
    }
    urlParams.append('reportingStartDate', values.reportingStartDate);
    urlParams.append('reportingStopDate', values.reportingStopDate);
    urlParams.append('mailClass', values.mailClass.map(obj => obj.value).join(','));
    urlParams.append('impc', values.impc ? values.impc.map(obj => obj.value).join(',') : '');
    return urlParams.toString();
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    updateSubmitted(true);

    if (!validations.all) {
      return newWarning('Please fill all required fields and try again!', 5);
    }

    const maxAllowedDate = addMonthsToDate(values.reportingStartDate, 3);
    if (isDateAfter(values.reportingStopDate, maxAllowedDate.format('DD/MM/YYYY'))) {
      return newWarning('Time frame for reporting start and stop date cannot exceed 3 months!', 5);
    }
    localStorage.setItem(RECEPTACLE_SEARCH_FORM_DATA, JSON.stringify(values));
    return push(language(`/receptacles/list${filter === '' ? '' : `/${filter}`}?${constructUrlParams()}`));
  };

  return (
    <AppContentAdaptive>
      <AppContentTitle>{getReceptacleListTitle(filter, 'Search')}</AppContentTitle>
      <form className="Form" onSubmit={submitHandler}>
        <Loading show={!loaded} />
        <ReportingDateRangeSection values={values} dateChangeHandler={changeDateValue} validations={validations} />
        <br />
        <CarrierOperator
          values={values}
          options={options}
          onSelectChange={selectChange}
          validations={validations}
          mailClassDisabled={isAllMailClassSelected}
          allMailClassCheckboxHandler={allMailClassCheckboxHandler}
          impcChecked={isImpcSelected}
          allImpcCheckBoxHandler={allImpcCheckBoxHandler}
          impcCheckboxDisableHandler={!hasImpcOptions()}
        />
        <br />
        <div className="ButtonWrapper">
          <Link to="/pre-settlement/list">
            <Button className="CancelButton">Cancel</Button>
          </Link>
          <Button className="SaveButton" onClick={submitHandler} disabled={!loaded} data-testid="submit-action">Submit</Button>
        </div>
      </form>
    </AppContentAdaptive>
  );
};

ReceptacleSearchCriteria.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      filter: PropTypes.string,
    }),
  }),
  newWarning: PropTypes.func,
};

export default ReceptacleSearchCriteria;
